<!-- SankeyChart.vue -->
<template>
  <div class="sankey-chart">
    <div class="chart-header">
      <h3>Cash Flow Diagram</h3>
      <div class="year-control">
        <label for="year-slider">Year: {{ selectedYear }}</label>
        <input
          id="year-slider"
          v-model="selectedYear"
          type="range"
          :min="minYear"
          :max="maxYear"
          step="1"
          class="year-slider"
        />
        <div class="year-range">
          <span>{{ minYear }}</span>
          <span>{{ maxYear }}</span>
        </div>
      </div>
    </div>

    <div class="chart-container">
      <div v-if="!hasData" class="no-data-message">
        No financial data available for {{ selectedYear }}
      </div>
      <HighChartsWrapper
        v-else
        name="sankey-flow"
        chart-type="sankey"
        :chart-options="sankeyOptions"
      />
    </div>

    <div class="flow-summary" v-if="hasData">
      <div class="summary-card income-summary">
        <h4>Total Income</h4>
        <p class="amount">{{ formatCurrency(totalIncome) }}</p>
      </div>
      <div class="summary-card expense-summary">
        <h4>Total Expenses</h4>
        <p class="amount">{{ formatCurrency(totalExpenses) }}</p>
      </div>
      <div class="summary-card net-summary" :class="{ negative: netFlow < 0 }">
        <h4>{{ netFlow >= 0 ? 'Net Savings' : 'Net Deficit' }}</h4>
        <p class="amount">{{ formatCurrency(Math.abs(netFlow)) }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';

export default {
  name: 'SankeyChart',
  components: {
    HighChartsWrapper
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    projection: {
      type: Array,
      default: () => []
    },
    formatCurrency: {
      type: Function,
      required: true
    },
    yearRows: {
      type: Array,
      default: () => []
    },
  },
  setup(props) {
    const currentYear = new Date().getFullYear();
    
    // Compute available years from items
    const availableYears = computed(() => {
      if (!props.items || props.items.length === 0) return [currentYear];
      
      const years = new Set();
      props.items.forEach(item => {
        const startYear = item.start_year || currentYear;
        const endYear = item.end_year || (currentYear + 10);
        
        for (let year = startYear; year <= endYear; year++) {
          years.add(year);
        }
      });
      
      return Array.from(years).sort((a, b) => a - b);
    });

    const minYear = computed(() => Math.min(...availableYears.value));
    const maxYear = computed(() => Math.max(...availableYears.value));
    const selectedYear = ref(currentYear);

    // Ensure selectedYear is within bounds
    watch([minYear, maxYear], () => {
      if (selectedYear.value < minYear.value) {
        selectedYear.value = minYear.value;
      } else if (selectedYear.value > maxYear.value) {
        selectedYear.value = maxYear.value;
      }
    });

    // Compute flows for selected year
    const yearlyFlows = computed(() => {
      const incomeFlows = [];
      const expenseFlows = [];

      const year = selectedYear.value;

      // Process regular items (income and expenses)
      props.items.forEach(item => {
        // Check if item is active in selected year
        if (year >= (item.start_year || currentYear) && 
            (item.end_year == null || year <= item.end_year)) {
          
          let amount = 0;
          const baseAmount = parseFloat(item.amount) || 0;
          
          // Calculate amount with growth
          if (item.frequency === 'One-time') {
            amount = year === item.start_year ? baseAmount : 0;
          } else if (item.frequency === 'Annually') {
            amount = baseAmount;
          } else if (item.frequency === 'Monthly') {
            amount = baseAmount * 12;
          }
          
          // Apply growth
          const periods = year - (item.start_year || currentYear);
          const growthRate = parseFloat(item.growth_rate) || 0;
          amount = amount * Math.pow(1 + growthRate, periods);
          
          if (amount > 0) {
            if (item.type === 'income') {
              incomeFlows.push({
                name: item.name || 'Unnamed Income',
                amount: amount,
                type: 'income'
              });
            } else if (item.type === 'expense') {
              expenseFlows.push({
                name: item.name || 'Unnamed Expense',
                amount: amount,
                type: 'expense'
              });
            }
          }
        }
      });

      // Add withdrawals from yearRows as income source
      if (props.yearRows && props.yearRows.length > 0) {
        const yearData = props.yearRows.find(row => Number(row.year) === Number(year));
        if (yearData && yearData.withdrawals && yearData.withdrawals > 0) {
          console.error('Adding portfolio withdrawals as income source:', yearData.withdrawals);
          incomeFlows.push({
            name: 'Portfolio Withdrawals',
            amount: yearData.withdrawals,
            type: 'withdrawal',
          });
        }
      }

      return { incomeFlows, expenseFlows };
    });

    const totalIncome = computed(() => 
      yearlyFlows.value.incomeFlows
        .filter(flow => flow.type !== 'withdrawal')
        .reduce((sum, flow) => sum + flow.amount, 0)
    );

    const totalExpenses = computed(() => 
      yearlyFlows.value.expenseFlows.reduce((sum, flow) => sum + flow.amount, 0)
    );

    const totalWithdrawals = computed(() =>
      yearlyFlows.value.incomeFlows
        .filter(flow => flow.type === 'withdrawal')
        .reduce((sum, flow) => sum + flow.amount, 0)
    );

    const netFlow = computed(() => {
      if (totalWithdrawals.value > 0) {
        return -totalWithdrawals.value;
      }
      return totalIncome.value - totalExpenses.value;
    });

    const hasData = computed(() => 
      yearlyFlows.value.incomeFlows.length > 0 || yearlyFlows.value.expenseFlows.length > 0
    );

    // Highcharts Sankey options
    const sankeyOptions = computed(() => {
      if (!hasData.value) return {};

      const { incomeFlows, expenseFlows } = yearlyFlows.value;

      const totalInflowsAmount = incomeFlows.reduce((sum, flow) => sum + flow.amount, 0);
      const totalInflowsLabel = `Total Inflows: $${totalInflowsAmount.toLocaleString()}`;

      
      // Create data for Sankey diagram
      const data = [];
      
      incomeFlows.forEach(income => {
        data.push([income.name, totalInflowsLabel, income.amount]);
      });
      
      expenseFlows.forEach(expense => {
        data.push([totalInflowsLabel, expense.name, expense.amount]);
      });
      
      if (netFlow.value > 0) {
        data.push([totalInflowsLabel, 'Net Savings', netFlow.value]);
      }

      return {
        title: {
          text: null
        },
        accessibility: {
          point: {
            valueDescriptionFormat: '{index}. {point.from} to {point.to}, {point.weight}.'
          }
        },
        tooltip: {
          headerFormat: null,
          pointFormat: '<b>{point.fromNode.name}</b> → <b>{point.toNode.name}</b><br/>Amount: <b>${point.weight:,.0f}</b>',
          nodeFormat: '<b>{point.name}</b><br/>Total: <b>${point.sum:,.0f}</b>'
        },
        series: [{
          keys: ['from', 'to', 'weight'],
          data: data.map(item => ({
            from: item[0],
            to: item[1],
            weight: item[2],
            // Custom color for each link based on destination
            color: item[1] === 'Net Savings' ? 'rgba(59, 130, 246, 0.3)' : // Blue for savings
                   expenseFlows.some(expense => expense.name === item[1]) ? 'rgba(239, 68, 68, 0.3)' : // Red for expenses
                   incomeFlows.some(income => income.type === 'withdrawal' && income.name === item[0]) ? 'rgba(255, 193, 7, 0.3)' : // Yellow for withdrawals
                   'rgba(34, 197, 94, 0.3)' // Green for regular income flows
          })),
          type: 'sankey',
          name: 'Cash Flow',
          nodeWidth: 20,
          nodePadding: 20,
          dataLabels: {
            enabled: true,
            color: '#000',
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
              textOutline: 'none'
            },
            // Only show node names, not values (to avoid duplication)
            nodeFormat: '{point.name}',
            // Show values on the flow lines
            format: '${point.weight:,.0f}',
            distance: 10,
            crop: false,
            overflow: 'allow'
          },
          // Custom node colors for specific nodes
          nodes: [
            // Income sources - translucent green
            ...incomeFlows.map(income => ({
              id: income.name,
              color: income.type === 'withdrawal' ? 'rgba(255, 193, 7, 0.7)' : 'rgba(34, 197, 94, 0.7)', // Yellow for withdrawals, green for income
              dataLabels: {
                color: '#000',
                style: {
                  fontWeight: 'bold'
                }
              }
            })),
            {
              id: totalInflowsLabel,
              color: 'rgba(107, 114, 128, 0.7)', // Translucent blue-gray
              dataLabels: {
                color: '#000',
                style: {
                  fontWeight: 'bold'
                }
              }
            },
            // Expenses - translucent red
            ...expenseFlows.map(expense => ({
              id: expense.name,
              color: 'rgba(239, 68, 68, 0.7)', // Translucent red
              dataLabels: {
                color: '#000',
                style: {
                  fontWeight: 'bold'
                }
              }
            })),
            // Net Savings - translucent blue
            ...(netFlow.value > 0 ? [{
              id: 'Net Savings',
              color: 'rgba(59, 130, 246, 0.7)', // Translucent blue
              dataLabels: {
                color: '#000',
                style: {
                  fontWeight: 'bold'
                }
              }
            }] : [])
          ]
        }],
        chart: {
          height: 500,
          marginLeft: 60,
          marginRight: 60
        },
        plotOptions: {
          sankey: {
            curveFactor: 0.6,
            linkOpacity: 0.3,
            nodeWidth: 20,
            // Remove the colorMode setting to use custom colors
            link: {
              // Individual link colors are now set in the data above
            }
          }
        }
      };
    });

    return {
      selectedYear,
      minYear,
      maxYear,
      hasData,
      totalIncome,
      totalExpenses,
      netFlow,
      sankeyOptions
    };
  }
};
</script>